import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { ZeltGrid } from "@/components/ZeltGrid";
import { FilterSidebar, type FilterState } from "@/components/FilterSidebar";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { useSeo, generateBreadcrumbSchema } from "@/hooks/useSeo";
import { ChevronDown, ArrowUpDown, Euro, Gauge, Tent, Weight, Compass } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Zelt, ZeltKategorie } from "@/data/zelte";
import { kategorien } from "@/data/zelte";

const sortOptions = [
  { value: "bewertung-desc", label: "Beste Bewertung" },
  { value: "bewertung-asc", label: "Bewertung aufsteigend" },
  { value: "preis-asc", label: "Preis aufsteigend" },
  { value: "preis-desc", label: "Preis absteigend" },
  { value: "name-asc", label: "Name A-Z" },
  { value: "name-desc", label: "Name Z-A" },
  { value: "personen-asc", label: "Personen aufsteigend" },
  { value: "personen-desc", label: "Personen absteigend" },
  { value: "gewicht-asc", label: "Gewicht leicht → schwer" },
  { value: "gewicht-desc", label: "Gewicht schwer → leicht" },
];

const formatEuro = (value: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

function getBuyingHint(filters: FilterState, resultCount: number): string {
  if (resultCount === 0) return "Lockern Sie Preis, Personenanzahl oder Kategorie, um passende Modelle zu finden.";
  if (filters.kategorie === "trekkingzelt" || filters.tags.includes("leicht")) {
    return "Achten Sie auf Gewicht, Packmaß und Wassersäule. Für lange Touren ist ein leichtes Zelt mit solider Regenreserve meist wichtiger als maximale Innenhöhe.";
  }
  if (filters.kategorie === "familienzelt" || (filters.personen && filters.personen >= 4)) {
    return "Vergleichen Sie Stehhöhe, Aufbauzeit und getrennte Schlafbereiche. Bei Familienzelten zahlt sich ein größerer Vorraum im Alltag schnell aus.";
  }
  if (filters.saison === 4) {
    return "Vier-Saison-Zelte sollten nicht nur warm, sondern auch windstabil sein. Hohe Wassersäule und stabile Konstruktion sind hier die wichtigsten Kennzahlen.";
  }
  if (filters.maxPreis && filters.maxPreis <= 100) {
    return "Im Einstiegsbudget lohnt der Blick auf Aufbauzeit, Packmaß und echte Regenreserve. Sehr günstige Zelte sind oft besser für Festival oder Sommerwochenenden.";
  }
  return "Nutzen Sie Preis, Gewicht und Wetterfestigkeit zusammen: Das beste Zelt ist selten das billigste, sondern das Modell mit den wenigsten Kompromissen für Ihren Einsatz.";
}

interface SortDropdownProps {
  sortBy: FilterState["sortBy"];
  order: FilterState["order"];
  onChange: (sortBy: FilterState["sortBy"], order: FilterState["order"]) => void;
}

function SortDropdown({ sortBy, order, onChange }: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentSort = `${sortBy}-${order}`;
  const currentLabel = sortOptions.find(o => o.value === currentSort)?.label || "Sortieren";

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setOpen(!open)}
        className={cn(
          "min-w-[180px] justify-between gap-2 transition-all duration-200",
          "hover:border-primary hover:shadow-md",
          open && "border-primary shadow-md"
        )}
      >
        <span className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <span className="hidden sm:inline">{currentLabel}</span>
          <span className="sm:hidden">Sortieren</span>
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 text-muted-foreground transition-transform duration-200",
          open && "rotate-180"
        )} />
      </Button>

      {/* Dropdown Menu */}
      <div className={cn(
        "absolute right-0 top-full mt-2 z-50 min-w-[200px]",
        "bg-background border rounded-lg shadow-lg overflow-hidden",
        "origin-top-right transition-all duration-200 ease-out",
        open
          ? "opacity-100 scale-100 translate-y-0"
          : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
      )}>
        <div className="p-1">
          {sortOptions.map((option, index) => {
            const [optSortBy, optOrder] = option.value.split("-") as [FilterState["sortBy"], FilterState["order"]];
            const isActive = sortBy === optSortBy && order === optOrder;

            return (
              <button
                key={option.value}
                onClick={() => {
                  onChange(optSortBy, optOrder);
                  setOpen(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-md text-sm cursor-pointer select-none",
                  "transition-all duration-150 ease-out",
                  isActive
                    ? "bg-primary text-primary-foreground font-medium"
                    : "hover:bg-muted active:scale-[0.98]"
                )}
                style={{
                  animationDelay: open ? `${index * 30}ms` : "0ms",
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function parseFiltersFromUrl(searchParams: URLSearchParams, initialKategorie?: ZeltKategorie | null): FilterState {
  return {
    kategorie: (searchParams.get("kategorie") as ZeltKategorie) || initialKategorie || null,
    personen: searchParams.get("personen") ? parseInt(searchParams.get("personen")!) : null,
    saison: searchParams.get("saison") ? (parseInt(searchParams.get("saison")!) as 3 | 4) : null,
    minPreis: searchParams.get("minPreis") ? parseInt(searchParams.get("minPreis")!) : null,
    maxPreis: searchParams.get("maxPreis") ? parseInt(searchParams.get("maxPreis")!) : null,
    tags: searchParams.get("tags") ? searchParams.get("tags")!.split(",") as any : [],
    marke: searchParams.get("marke") || null,
    maxGewicht: searchParams.get("maxGewicht") ? parseFloat(searchParams.get("maxGewicht")!) : null,
    minWassersaeule: searchParams.get("minWassersaeule") ? parseInt(searchParams.get("minWassersaeule")!) : null,
    sortBy: (searchParams.get("sortBy") as FilterState["sortBy"]) || "bewertung",
    order: (searchParams.get("order") as FilterState["order"]) || "desc",
  };
}

interface ZelteListeProps {
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  vergleichIds: string[];
  initialKategorie?: ZeltKategorie | null;
  initialSuche?: string | null;
}

export function ZelteListe({ onNavigate, onToggleVergleich, vergleichIds, initialKategorie, initialSuche }: ZelteListeProps) {
  const [zelte, setZelte] = useState<Zelt[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>(() =>
    parseFiltersFromUrl(new URLSearchParams(window.location.search), initialKategorie)
  );

  // Build query string from filters
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.kategorie) params.set("kategorie", filters.kategorie);
    if (filters.personen) params.set("personen", filters.personen.toString());
    if (filters.saison) params.set("saison", filters.saison.toString());
    if (filters.minPreis) params.set("minPreis", filters.minPreis.toString());
    if (filters.maxPreis) params.set("maxPreis", filters.maxPreis.toString());
    if (filters.tags.length > 0) params.set("tags", filters.tags.join(","));
    if (filters.marke) params.set("marke", filters.marke);
    if (filters.maxGewicht) params.set("maxGewicht", filters.maxGewicht.toString());
    if (filters.minWassersaeule) params.set("minWassersaeule", filters.minWassersaeule.toString());
    if (initialSuche) params.set("suche", initialSuche);
    if (filters.sortBy) params.set("sortBy", filters.sortBy);
    if (filters.order) params.set("order", filters.order);
    return params.toString();
  }, [filters, initialSuche]);

  // Sync filters to URL (without scrolling or pushing history on every filter change)
  useEffect(() => {
    const params = new URLSearchParams();
    if (initialSuche) params.set("suche", initialSuche);
    if (filters.kategorie) params.set("kategorie", filters.kategorie);
    if (filters.personen) params.set("personen", filters.personen.toString());
    if (filters.saison) params.set("saison", filters.saison.toString());
    if (filters.minPreis) params.set("minPreis", filters.minPreis.toString());
    if (filters.maxPreis) params.set("maxPreis", filters.maxPreis.toString());
    if (filters.tags.length > 0) params.set("tags", filters.tags.join(","));
    if (filters.marke) params.set("marke", filters.marke);
    if (filters.maxGewicht) params.set("maxGewicht", filters.maxGewicht.toString());
    if (filters.minWassersaeule) params.set("minWassersaeule", filters.minWassersaeule.toString());
    if (filters.sortBy !== "bewertung") params.set("sortBy", filters.sortBy);
    if (filters.order !== "desc") params.set("order", filters.order);
    const qs = params.toString();
    const newUrl = `/zelte${qs ? `?${qs}` : ""}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, "", newUrl);
    }
  }, [filters, initialSuche]);

  // Fetch zelte when filters change
  useEffect(() => {
    setLoading(true);

    fetch(`/api/zelte${queryString ? `?${queryString}` : ""}`)
      .then(res => res.json())
      .then(data => {
        setZelte(data.data || data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [queryString]);

  // Update filter when initialKategorie changes
  useEffect(() => {
    if (initialKategorie) {
      setFilters(prev => ({ ...prev, kategorie: initialKategorie }));
    }
  }, [initialKategorie]);

  const kategorieName = filters.kategorie
    ? kategorien.find(k => k.id === filters.kategorie)?.name
    : null;

  const breadcrumbItems = kategorieName
    ? [{ label: "Zelte", href: "/zelte" }, { label: kategorieName }]
    : [{ label: "Zelte" }];

  const title = initialSuche
    ? `Suchergebnisse für "${initialSuche}"`
    : kategorieName
      ? kategorieName
      : "Alle Zelte";

  const buyingGuide = useMemo(() => {
    if (loading || zelte.length === 0) return null;

    const averagePrice = Math.round(zelte.reduce((sum, zelt) => sum + zelt.preis, 0) / zelte.length);
    const averageWeight = zelte.reduce((sum, zelt) => sum + zelt.gewicht, 0) / zelte.length;
    const weatherproofCount = zelte.filter(zelt => zelt.wassersaeule >= 5000 || zelt.saison === 4).length;
    const lightweightCount = zelte.filter(zelt => zelt.gewicht <= 2.5).length;
    const topRated = [...zelte].sort((a, b) => b.bewertung - a.bewertung || a.preis - b.preis)[0];
    const bestValue = [...zelte].sort((a, b) => {
      const scoreA = (a.bewertung * 100) / Math.max(a.preis, 1);
      const scoreB = (b.bewertung * 100) / Math.max(b.preis, 1);
      return scoreB - scoreA || b.bewertungAnzahl - a.bewertungAnzahl;
    })[0];

    return {
      averagePrice,
      averageWeight,
      weatherproofCount,
      lightweightCount,
      topRated,
      bestValue,
      hint: getBuyingHint(filters, zelte.length),
    };
  }, [filters, loading, zelte]);

  // SEO
  useSeo({
    title: initialSuche
      ? `${title} | zelt.kaufen`
      : kategorieName
        ? `${kategorieName} kaufen - Vergleich & Bewertungen | zelt.kaufen`
        : "Alle Zelte - Vergleich & Bewertungen | zelt.kaufen",
    description: initialSuche
      ? `${zelte.length} Suchergebnisse für "${initialSuche}"`
      : kategorieName
        ? `${zelte.length} ${kategorieName} im Vergleich. Finden Sie das perfekte Zelt für Ihre Bedürfnisse.`
        : `${zelte.length} Zelte im Vergleich. Camping, Trekking, Festival - für jeden Einsatz das richtige Zelt.`,
    keywords: kategorieName ? `${kategorieName}, Zelt kaufen, Campingzelt` : "Zelt kaufen, Zeltvergleich, Campingausrüstung",
    type: "website",
    schema: generateBreadcrumbSchema(
      kategorieName
        ? [
            { name: "Startseite", url: window.location.origin },
            { name: "Zelte", url: `${window.location.origin}/zelte` },
            { name: kategorieName, url: window.location.href },
          ]
        : [
            { name: "Startseite", url: window.location.origin },
            { name: "Zelte", url: window.location.href },
          ]
    ),
  });

  return (
    <Container className="py-8">
      <Breadcrumb items={breadcrumbItems} className="mb-4" />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h2 className="font-semibold">Filter</h2>
              <FilterSidebar filters={filters} onChange={setFilters} mobile />
            </div>
            <div className="hidden lg:block">
              <FilterSidebar filters={filters} onChange={setFilters} />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{title}</h1>
              <p className="text-muted-foreground mt-1">
                {loading ? "Lädt..." : `${zelte.length} Zelte gefunden`}
              </p>
            </div>

            {/* Sort Dropdown */}
            <SortDropdown
              sortBy={filters.sortBy}
              order={filters.order}
              onChange={(sortBy, order) => setFilters(prev => ({ ...prev, sortBy, order }))}
            />
          </div>

          {buyingGuide && (
            <section className="mb-6 rounded-2xl border bg-muted/30 p-4 md:p-5">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-2xl">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
                    Kaufberatung für diese Auswahl
                  </p>
                  <h2 className="text-lg font-bold mb-2">
                    {zelte.length} Modelle, {formatEuro(buyingGuide.averagePrice)} Durchschnittspreis
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {buyingGuide.hint}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-2 min-w-0 lg:min-w-[420px]">
                  <div className="rounded-xl bg-background/70 px-3 py-3">
                    <Euro className="h-4 w-4 text-primary mb-2" />
                    <div className="text-sm font-bold">{formatEuro(buyingGuide.averagePrice)}</div>
                    <div className="text-xs text-muted-foreground">Ø Preis</div>
                  </div>
                  <div className="rounded-xl bg-background/70 px-3 py-3">
                    <Weight className="h-4 w-4 text-primary mb-2" />
                    <div className="text-sm font-bold">{buyingGuide.averageWeight.toFixed(1)} kg</div>
                    <div className="text-xs text-muted-foreground">Ø Gewicht</div>
                  </div>
                  <div className="rounded-xl bg-background/70 px-3 py-3">
                    <Gauge className="h-4 w-4 text-primary mb-2" />
                    <div className="text-sm font-bold">{buyingGuide.weatherproofCount}</div>
                    <div className="text-xs text-muted-foreground">wetterfest</div>
                  </div>
                  <div className="rounded-xl bg-background/70 px-3 py-3">
                    <Tent className="h-4 w-4 text-primary mb-2" />
                    <div className="text-sm font-bold">{buyingGuide.lightweightCount}</div>
                    <div className="text-xs text-muted-foreground">leicht</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <button
                  onClick={() => onNavigate(`/zelte/${buyingGuide.topRated.slug}`)}
                  className="text-left rounded-xl bg-background/70 px-4 py-3 transition-colors hover:bg-background"
                >
                  <span className="text-xs font-semibold text-muted-foreground">Top bewertet</span>
                  <span className="mt-1 flex items-center justify-between gap-3">
                    <span className="font-semibold">{buyingGuide.topRated.name}</span>
                    <span className="text-sm text-primary">{buyingGuide.topRated.bewertung} / 5</span>
                  </span>
                </button>
                <button
                  onClick={() => onNavigate(`/zelte/${buyingGuide.bestValue.slug}`)}
                  className="text-left rounded-xl bg-background/70 px-4 py-3 transition-colors hover:bg-background"
                >
                  <span className="text-xs font-semibold text-muted-foreground">Preis-Leistung</span>
                  <span className="mt-1 flex items-center justify-between gap-3">
                    <span className="font-semibold">{buyingGuide.bestValue.name}</span>
                    <span className="text-sm text-primary">{formatEuro(buyingGuide.bestValue.preis)}</span>
                  </span>
                </button>
              </div>
            </section>
          )}

          <div className="mb-6 rounded-2xl border bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Compass className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Unsicher, welches Zelt passt?</p>
                <p className="text-sm text-muted-foreground">In vier Fragen zur persönlichen Empfehlung.</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate("/finder")}
              className="rounded-xl self-start sm:self-auto"
            >
              Zelt-Finder starten
            </Button>
          </div>

          <ZeltGrid
            zelte={zelte}
            onNavigate={onNavigate}
            onToggleVergleich={onToggleVergleich}
            vergleichIds={vergleichIds}
            loading={loading}
          />
        </main>
      </div>
    </Container>
  );
}
